import React, { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, Clock3, IndianRupee, ShieldCheck, Wallet } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axiosClient from "../../api/axiosClient";
import PageHeader from "../../components/common/PageHeader";

const SUMMARY_ENDPOINT = "/payment/wallet";
const LEDGER_ENDPOINT = "/payment/wallet/ledger";
const TOP_UP_CHECKOUT_ENDPOINT = "/payment/wallet/top-up/checkout";
const SUBSCRIPTION_ACCESS_ENDPOINT = "/payment/subscription/access";
const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 20;
const WALLET_TOP_UP_MIN_AMOUNT = 50;
const TOP_UP_PRESET_AMOUNTS = [50, 100, 250, 300, 500];

const unwrapResponse = res => {
  const body = res?.data ?? {};
  const ok = typeof body.ok === "boolean" ? body.ok : true;
  const data = body?.data ?? body;
  const message = body?.message || data?.message || "";
  return { ok, data, message };
};

const formatCurrency = amount =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number.isFinite(Number(amount)) ? Number(amount) : 0);

const formatDateTime = value => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const getEntryTypeLabel = entryType => {
  const normalized = String(entryType || "").trim().toLowerCase();
  if (normalized === "debit") return "Wallet Deduction";
  if (normalized === "credit") return "Wallet top-up";
  return "Wallet Activity";
};

const isTopUpEntry = row =>
  String(row?.entryType || "").trim().toLowerCase() === "credit";

const formatProviderReportedAmount = (amount, currency) => {
  if (amount == null) return null;
  const normalizedCurrency = String(currency || "INR").trim().toUpperCase() || "INR";

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: normalizedCurrency,
    maximumFractionDigits: 2,
  }).format(Number(amount));
};

export default function WalletPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState("");
  const [topUpSuccessVisible, setTopUpSuccessVisible] = useState(false);

  const [ledgerPage, setLedgerPage] = useState(DEFAULT_PAGE);
  const [ledger, setLedger] = useState({ page: DEFAULT_PAGE, pageSize: DEFAULT_PAGE_SIZE, hasMore: false, items: [] });
  const [ledgerLoading, setLedgerLoading] = useState(true);
  const [ledgerError, setLedgerError] = useState("");
  const [topUpOpen, setTopUpOpen] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState(null);
  const [topUpCustomAmount, setTopUpCustomAmount] = useState("");
  const [topUpError, setTopUpError] = useState("");
  const [topUpLoading, setTopUpLoading] = useState(false);
  const [subscriptionEligible, setSubscriptionEligible] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState("An active subscription is required before adding wallet funds.");
  const topUpSectionRef = useRef(null);
  const topUpButtonRef = useRef(null);
  const topUpPopoverRef = useRef(null);

  const walletStatusLabel = !summary?.exists
    ? "Not Active"
    : summary?.status === "NotCreated"
      ? "Not Active"
      : summary?.status || "Not Active";

  const loadSummary = useCallback(async () => {
    try
    {
      setSummaryLoading(true);
      setSummaryError("");

      const res = await axiosClient.get(SUMMARY_ENDPOINT);
      const { ok, data, message } = unwrapResponse(res);
      if (!ok) {
        throw new Error(message || "Failed to load wallet summary.");
      }

      setSummary(data);
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || "Failed to load wallet summary.";
      setSummaryError(message);
      toast.error(message);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const loadLedger = useCallback(async page => {
    try
    {
      setLedgerLoading(true);
      setLedgerError("");

      const res = await axiosClient.get(LEDGER_ENDPOINT, {
        params: {
          page,
          pageSize: DEFAULT_PAGE_SIZE,
        },
      });

      const { ok, data, message } = unwrapResponse(res);
      if (!ok) {
        throw new Error(message || "Failed to load wallet activity.");
      }

      setLedger({
        page: data?.page ?? page,
        pageSize: data?.pageSize ?? DEFAULT_PAGE_SIZE,
        hasMore: !!data?.hasMore,
        items: Array.isArray(data?.items) ? data.items : [],
      });
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || "Failed to load wallet activity.";
      setLedgerError(message);
      toast.error(message);
    } finally {
      setLedgerLoading(false);
    }
  }, []);

  const loadSubscriptionEligibility = useCallback(async () => {
    try
    {
      const res = await axiosClient.get(SUBSCRIPTION_ACCESS_ENDPOINT, {
        headers: { "x-suppress-403-toast": true },
      });

      const { ok, data, message } = unwrapResponse(res);
      if (!ok) {
        setSubscriptionEligible(false);
        setSubscriptionMessage(message || "An active subscription is required before adding wallet funds.");
        return;
      }

      const allowed = !!data?.allowed;
      const backendMessage =
        data?.message || message || "An active subscription is required before adding wallet funds.";

      setSubscriptionEligible(allowed);
      setSubscriptionMessage(allowed ? "" : backendMessage);
    } catch (error) {
      const message =
        error?.response?.data?.data?.message ||
        error?.response?.data?.message ||
        "An active subscription is required before adding wallet funds.";

      setSubscriptionEligible(false);
      setSubscriptionMessage(message);
    }
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    loadSubscriptionEligibility();
  }, [loadSubscriptionEligibility]);

  useEffect(() => {
    loadLedger(ledgerPage);
  }, [ledgerPage, loadLedger]);

  useEffect(() => {
    if (!topUpOpen || typeof document === "undefined") return undefined;

    const handlePointerDown = event => {
      const target = event.target;
      if (topUpPopoverRef.current?.contains(target) || topUpButtonRef.current?.contains(target)) {
        return;
      }

      setTopUpOpen(false);
      setTopUpError("");
    };

    const handleEscape = event => {
      if (event.key !== "Escape") return;
      setTopUpOpen(false);
      setTopUpError("");
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [topUpOpen]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search || "");
    if (searchParams.get("topupSuccess") !== "1") return;

    setTopUpSuccessVisible(true);
    toast.success("Funds added successfully");
    setTopUpOpen(false);
    setTopUpAmount(null);
    setTopUpCustomAmount("");
    setTopUpError("");
    setLedgerPage(DEFAULT_PAGE);
    void loadSummary();
    void loadLedger(DEFAULT_PAGE);
    void loadSubscriptionEligibility();
    navigate("/app/settings/wallet", { replace: true });
  }, [loadLedger, loadSubscriptionEligibility, loadSummary, location.search, navigate]);

  const reservedBalance = Number(summary?.reservedBalance || 0);
  const showReservedBalance = reservedBalance > 0;
  const showNegativeBalanceWarning = !summaryLoading && !!summary?.exists && !!summary?.isNegativeBalance;
  const showLowBalanceWarning = !summaryLoading && !!summary?.exists && !showNegativeBalanceWarning && !!summary?.isLowBalance;

  const openTopUpPanel = useCallback(() => {
    if (!subscriptionEligible) {
      setTopUpOpen(false);
      setTopUpError(subscriptionMessage);
      toast.error(subscriptionMessage);
      return;
    }

    setTopUpOpen(true);
    setTopUpError("");

    const focusSection = () => {
      const element = topUpSectionRef.current;
      if (!element) return;
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      if (typeof element.focus === "function") {
        element.focus();
      }
    };

    if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(focusSection);
      return;
    }

    focusSection();
  }, [subscriptionEligible, subscriptionMessage]);

  const resolveTopUpAmount = useCallback(() => {
    if (topUpCustomAmount.trim().length > 0) {
      const parsedAmount = Number(topUpCustomAmount);
      if (!Number.isFinite(parsedAmount) || !Number.isInteger(parsedAmount)) {
        return null;
      }

      return parsedAmount;
    }

    return Number.isInteger(topUpAmount) ? topUpAmount : null;
  }, [topUpAmount, topUpCustomAmount]);

  const handleTopUpSubmit = async () => {
    const selectedAmount = resolveTopUpAmount();

    if (!Number.isInteger(selectedAmount)) {
      setTopUpError("Select or enter an amount to add.");
      return;
    }

    if (selectedAmount < WALLET_TOP_UP_MIN_AMOUNT) {
      setTopUpError(`Enter an amount of INR ${WALLET_TOP_UP_MIN_AMOUNT} or more.`);
      return;
    }

    if (!subscriptionEligible) {
      setTopUpError(subscriptionMessage);
      return;
    }

    try
    {
      setTopUpSuccessVisible(false);
      setTopUpLoading(true);
      setTopUpError("");

      const res = await axiosClient.post(TOP_UP_CHECKOUT_ENDPOINT, {
        amount: selectedAmount,
      });

      const { ok, data, message } = unwrapResponse(res);
      if (!ok) {
        throw new Error(message || "Failed to start wallet top-up.");
      }

      const checkoutSessionId =
        data?.checkoutSessionId || data?.sessionId || data?.id || null;

      if (!checkoutSessionId) {
        throw new Error("Checkout session id not returned by server.");
      }

      navigate(
        `/app/payment/status?source=wallet&checkoutSessionId=${encodeURIComponent(checkoutSessionId)}&sessionId=${encodeURIComponent(checkoutSessionId)}`,
      );
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || "Failed to start wallet top-up.";
      setTopUpError(message);
      toast.error(message);
    } finally {
      setTopUpLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f3f4f6] py-6">
      <section className="px-4">
        <div className="mx-auto max-w-[1240px] space-y-6">
          <div className="px-4 pt-4 pb-1">
            <PageHeader
              icon={Wallet}
              title="Wallet"
              subtitle="Track your prepaid INR balance and wallet activity for conversation-based deductions."
            />
          </div>

          {summaryError ? (
            <div className="rounded-[10px] border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-700">
              {summaryError}
            </div>
          ) : null}

          {topUpSuccessVisible ? (
            <div className="mx-4 rounded-[10px] border border-emerald-200 bg-emerald-50 px-5 py-4 text-emerald-900 shadow-sm">
              <div className="text-sm font-semibold">Funds added successfully</div>
              <div className="mt-1 text-sm text-emerald-800">
                Your wallet balance has been updated.
              </div>
            </div>
          ) : null}

          {showNegativeBalanceWarning ? (
            <div className="mx-4 rounded-[10px] border border-rose-200 bg-rose-50 px-5 py-4 text-rose-900 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div className="rounded-xl bg-rose-100 p-2 text-rose-700">
                  <AlertTriangle className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-semibold">Wallet balance is negative</div>
                  <div className="mt-1 text-sm text-rose-800">
                    Your wallet balance is below zero. Sending can continue for now, but new conversation charges will still be deducted to the wallet.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={openTopUpPanel}
                  className="shrink-0 rounded-lg bg-rose-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-rose-700"
                >
                  Add Funds
                </button>
              </div>
            </div>
          ) : null}

          {showLowBalanceWarning ? (
            <div className="mx-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-amber-900 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <div className="rounded-lg bg-amber-100 p-1.5 text-amber-700">
                  <AlertTriangle className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1 text-sm leading-5">
                  <span className="font-semibold">Low wallet balance.</span>{" "}
                  <span className="text-amber-800">
                    Your prepaid wallet balance is running low. Consider adding
                    funds soon for continued conversation-based deductions.
                  </span>
                </div>
                <button
                  type="button"
                  onClick={openTopUpPanel}
                  className="shrink-0 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-900 transition hover:bg-amber-100"
                >
                  Add Funds
                </button>
              </div>
            </div>
          ) : null}

          <div className="grid gap-3 px-4 xl:grid-cols-[1.05fr_0.8fr_0.85fr]">
            <div className="relative overflow-visible rounded-lg border border-slate-200 bg-[linear-gradient(145deg,rgba(255,255,255,0.98),rgba(240,253,250,0.94),rgba(255,255,255,0.98))] p-4 shadow-sm">
              <div className="relative space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className="rounded-lg bg-emerald-600 p-2 text-white shadow-sm">
                      <IndianRupee className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                        Wallet Balance
                      </div>
                      <div className="mt-1 text-sm font-semibold text-slate-900">Prepaid INR wallet</div>
                    </div>
                  </div>
                  <div className="relative shrink-0">
                    <button
                      ref={topUpButtonRef}
                      type="button"
                      onClick={() => {
                        if (!subscriptionEligible) {
                          setTopUpError(subscriptionMessage);
                          toast.error(subscriptionMessage);
                          return;
                        }

                        if (!topUpOpen) {
                          setTopUpCustomAmount("");
                          setTopUpAmount(null);
                        }

                        setTopUpOpen(open => !open);
                        setTopUpError("");
                      }}
                      className="inline-flex rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                      disabled={!subscriptionEligible}
                    >
                      {topUpOpen ? "Close" : "Add Funds"}
                    </button>

                    {topUpOpen ? (
                      <div
                        ref={topUpPopoverRef}
                        className="absolute right-0 top-full z-30 mt-3 w-[380px] max-w-[calc(100vw-2rem)] rounded-[10px] border border-slate-200 bg-white p-4 shadow-[0_26px_60px_rgba(15,23,42,0.18)]"
                      >
                        <div className="absolute -top-2 right-8 h-4 w-4 rotate-45 border-l border-t border-slate-200 bg-white" />
                        <div className="relative space-y-4">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-400">
                                Add Funds
                              </div>
                              <div className="mt-1 text-sm font-semibold text-slate-900">
                                Select a preset or enter any INR amount
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                setTopUpOpen(false);
                                setTopUpError("");
                              }}
                              className="rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-500 transition hover:border-slate-300 hover:text-slate-700"
                            >
                              Close
                            </button>
                          </div>

                          <div className="grid grid-cols-3 gap-2">
                            {TOP_UP_PRESET_AMOUNTS.map(amount => {
                              const isSelected = topUpAmount === amount && topUpCustomAmount.trim().length === 0;
                              return (
                                <button
                                  key={amount}
                                  type="button"
                                  onClick={() => {
                                    setTopUpAmount(amount);
                                    setTopUpCustomAmount("");
                                    if (topUpError) {
                                      setTopUpError("");
                                    }
                                  }}
                                  className={`rounded-[10px] border px-3 py-3 text-sm font-semibold transition ${
                                    isSelected
                                      ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                                      : "border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300 hover:bg-white"
                                  }`}
                                >
                                  {formatCurrency(amount)}
                                </button>
                              );
                            })}
                          </div>

                          <div className="space-y-2">
                            <label htmlFor="wallet-top-up-custom-amount" className="block text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">
                              Custom Amount
                            </label>
                            <div className="relative">
                              <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-sm font-medium text-slate-500">
                                INR
                              </span>
                              <input
                                id="wallet-top-up-custom-amount"
                                type="number"
                                min={WALLET_TOP_UP_MIN_AMOUNT}
                                step="1"
                                inputMode="numeric"
                                value={topUpCustomAmount}
                                onChange={event => {
                                  const nextValue = event.target.value.replace(/[^\d]/g, "");
                                  setTopUpCustomAmount(nextValue);
                                  setTopUpAmount(nextValue.length > 0 ? Number(nextValue) : null);
                                  if (topUpError) {
                                    setTopUpError("");
                                  }
                                }}
                                placeholder={`Minimum INR ${WALLET_TOP_UP_MIN_AMOUNT}`}
                                className="w-full rounded-[10px] border border-slate-200 bg-slate-50 py-3 pr-4 pl-14 text-sm text-slate-700 outline-none transition focus:border-emerald-500 focus:bg-white focus:ring-2 focus:ring-emerald-100"
                              />
                            </div>
                          </div>

                          <div className="flex items-center justify-between gap-3">
                            <div className="text-xs text-slate-500">
                              Minimum top-up is INR {WALLET_TOP_UP_MIN_AMOUNT}.
                            </div>
                            <button
                              type="button"
                              onClick={handleTopUpSubmit}
                              disabled={topUpLoading || !subscriptionEligible}
                              className="rounded-full bg-emerald-600 px-4 py-2 text-xs font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-70"
                            >
                              {topUpLoading ? "Starting..." : "Proceed"}
                            </button>
                          </div>

                          {topUpError ? (
                            <div className="text-sm text-rose-700">{topUpError}</div>
                          ) : null}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>

                {summaryLoading ? (
                  <div className="text-sm text-slate-500">Loading wallet summary...</div>
                ) : (
                  <>
                    <div ref={topUpSectionRef} tabIndex={-1} className="outline-none">
                      <div className="text-[30px] font-semibold tracking-[-0.035em] text-slate-950">
                        {formatCurrency(summary?.availableBalance ?? 0)}
                      </div>
                      <div className="mt-1 text-xs text-slate-500">
                        Ready for conversation-based deductions.
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                      {showReservedBalance ? (
                        <span className="rounded-full border border-slate-200 bg-white px-3 py-1.5 font-medium text-slate-700">
                          Reserved {formatCurrency(reservedBalance)}
                        </span>
                      ) : null}
                      {!subscriptionEligible ? (
                        <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 font-medium text-amber-700">
                          {subscriptionMessage}
                        </span>
                      ) : (
                        <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 font-medium text-emerald-700">
                          Subscription access confirmed
                        </span>
                      )}
                    </div>
                  </>
                )}

              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-[linear-gradient(150deg,rgba(255,255,255,0.98),rgba(240,249,255,0.96),rgba(255,255,255,0.98))] p-4 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-sky-100 p-2 text-sky-700">
                  <ShieldCheck className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Status</div>
                  {summaryLoading ? (
                    <div className="mt-2 text-sm text-slate-500">Loading wallet status...</div>
                  ) : (
                    <>
                      <div className="mt-2 text-2xl font-semibold tracking-[-0.035em] text-slate-950">
                        {walletStatusLabel}
                      </div>
                      <div className="mt-1 text-xs leading-5 text-slate-600">
                        {summary?.exists ? "Wallet is available for prepaid deductions." : "Wallet has not been created yet."}
                      </div>
                      <div className="mt-3 inline-flex rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600">
                        {summary?.exists ? "Prepaid deductions enabled" : "Awaiting first credit"}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-[linear-gradient(150deg,rgba(255,255,255,0.98),rgba(255,251,235,0.96),rgba(255,255,255,0.98))] p-4 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-amber-100 p-2 text-amber-700">
                  <Clock3 className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Last Updated</div>
                  {summaryLoading ? (
                    <div className="mt-2 text-sm text-slate-500">Loading update time...</div>
                  ) : (
                    <>
                      <div className="mt-2 text-xl font-semibold tracking-[-0.025em] leading-tight text-slate-950">
                        {formatDateTime(summary?.updatedAtUtc)}
                      </div>
                      <div className="mt-1 text-xs leading-5 text-slate-600">
                        Most recent wallet balance update for this workspace.
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="px-4">
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
              <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                <div>
                  <h2 className="text-base font-semibold text-slate-900">Wallet Top-up History</h2>
                  <p className="mt-1 text-xs text-slate-500">
                    Recent wallet funding entries for this workspace.
                  </p>
                </div>
              </div>

              {ledgerError ? (
                <div className="px-4 py-6 text-sm text-rose-700">{ledgerError}</div>
              ) : ledgerLoading ? (
                <div className="px-4 py-6 text-sm text-slate-500">Loading wallet top-up history...</div>
              ) : ledger.items.filter(isTopUpEntry).length === 0 ? (
                <div className="px-4 py-10 text-center">
                  <div className="text-base font-semibold text-slate-900">No wallet top-ups yet</div>
                  <div className="mt-2 text-sm text-slate-500">
                    Wallet funding entries will appear here after you add funds.
                  </div>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Date</th>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Type</th>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Amount</th>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Details</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 bg-white">
                        {ledger.items.filter(isTopUpEntry).map(row => {
                          const providerAmount = formatProviderReportedAmount(
                            row.providerReportedAmountInInr,
                            row.providerReportedCurrency,
                          );

                          return (
                            <tr key={row.ledgerEntryId}>
                              <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-700">
                                {formatDateTime(row.occurredAtUtc)}
                              </td>
                              <td className="px-4 py-3 text-sm font-medium text-slate-900">
                                {getEntryTypeLabel(row.entryType)}
                              </td>
                              <td className="whitespace-nowrap px-4 py-3 text-sm font-semibold text-slate-900">
                                {formatCurrency(row.amountInInr)}
                              </td>
                              <td className="px-4 py-3 text-sm text-slate-700">
                                <div>{row.description || "Wallet activity"}</div>
                                {providerAmount ? (
                                  <div className="mt-1 text-xs text-slate-500">
                                    Provider-reported amount: {providerAmount}
                                  </div>
                                ) : null}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3">
                    <div className="text-sm text-slate-500">Page {ledger.page}</div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setLedgerPage(page => Math.max(1, page - 1))}
                        disabled={ledger.page <= 1 || ledgerLoading}
                        className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <button
                        type="button"
                        onClick={() => setLedgerPage(page => page + 1)}
                        disabled={!ledger.hasMore || ledgerLoading}
                        className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
